import { useMemo } from 'react';

interface UseInputLockStateParams {
    tabSelected: 'internal' | 'reply_external';
    allowInputInExternalReply: boolean;
    assignee: string;
    showWhatsAppInactivityTemplateState: boolean;
    ownExtensionKey: string;
}

interface InputLockState {
    isLocked: boolean;
    shouldShowLockIndicator: boolean;
    additionalCssClass: string;
}

/**
 * Hook to calculate the lock state for the ticket input field
 * Determines if input should be locked, what CSS class to apply, and whether to show lock indicator
 */
export const useInputLockState = ({
    tabSelected,
    allowInputInExternalReply,
    assignee,
    showWhatsAppInactivityTemplateState,
    ownExtensionKey,
}: UseInputLockStateParams): InputLockState => {
    return useMemo(() => {
        // Internal notes are never locked
        if (tabSelected === 'internal' || allowInputInExternalReply) {
            return {
                isLocked: false,
                shouldShowLockIndicator: false,
                additionalCssClass: '',
            };
        }

        // External reply is restricted
        // Unassigned ticket - allow self-assignment on click
        if (assignee === 'unassigned') {
            return {
                isLocked: true,
                shouldShowLockIndicator: false,
                additionalCssClass: 'cursor-pointer',
            };
        }

        // WhatsApp template required, or assigned to someone else - locked
        if (
            showWhatsAppInactivityTemplateState ||
            assignee !== ownExtensionKey
        ) {
            return {
                isLocked: true,
                shouldShowLockIndicator: true,
                additionalCssClass: 'cursor-default',
            };
        }

        // Default - unlocked
        return {
            isLocked: false,
            shouldShowLockIndicator: false,
            additionalCssClass: '',
        };
    }, [
        tabSelected,
        allowInputInExternalReply,
        assignee,
        showWhatsAppInactivityTemplateState,
        ownExtensionKey,
    ]);
};
